import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/UI';

export function Landing() {
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://frm.ninja/f/c08d26c2-030e-4c1f-bb7a-f4bc3d27baea', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setContactSubmitted(true);
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch {
      alert('Failed to send message. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center sm:justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src="/logo.png" alt="" className="h-8 w-8" />
              <img src="/logo-text.png" alt="Statsmatic" className="h-6" />
            </Link>
            <div className="hidden sm:flex items-center space-x-6">
              <a href="#features" className="text-gray-600 hover:text-gray-900 font-medium">
                Features
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 font-medium">
                Pricing
              </a>
              <a href="#faq" className="text-gray-600 hover:text-gray-900 font-medium">
                FAQ
              </a>
              <a href="#contact" className="text-gray-600 hover:text-gray-900 font-medium">
                Contact
              </a>
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Log in
              </Link>
              <Link to="/signup">
                <Button>Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-8 pb-8 lg:pt-12 lg:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight text-center">
            Simple analytics for
            <span className="text-primary-600"> SaaS portfolios</span>
          </h1>

          <div className="mt-12 lg:mt-16 grid lg:grid-cols-2 items-start">
            {/* Content */}
            <div className="text-center flex flex-col items-center lg:pl-32">
              <p className="text-xl text-gray-600">
                Track all your websites from a single dashboard. Privacy-first, lightweight,
                and built for indie hackers running multiple products.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg">Start for free</Button>
                </Link>
                <a href="#features">
                  <Button variant="secondary" size="lg">
                    See features
                  </Button>
                </a>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                No credit card required. Free plan includes 5,000 requests/month.
              </p>
            </div>

            {/* Hero Image */}
            <div className="lg:pl-8 flex justify-center">
              <img
                src="/hero1.png"
                alt="Statsmatic dashboard"
                className="rounded-xl shadow-2xl border border-gray-200 w-3/4"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="pt-10 pb-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">
              Everything you need, nothing you don't
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Focus on what matters: pageviews, visitors, and conversions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<UnlimitedIcon />}
              title="Unlimited Sites"
              description="Track as many websites as you want. No per-site pricing or artificial limits."
            />
            <FeatureCard
              icon={<PrivacyIcon />}
              title="Privacy-First"
              description="No cookies, GDPR compliant out of the box. Respect your visitors' privacy."
            />
            <FeatureCard
              icon={<LightweightIcon />}
              title="Lightweight Script"
              description="Under 1KB tracking script. Won't slow down your website or hurt SEO."
            />
            <FeatureCard
              icon={<DashboardIcon />}
              title="Unified Dashboard"
              description="See all your sites at a glance. Drill down into individual site analytics."
            />
            <FeatureCard
              icon={<TeamIcon />}
              title="Team Collaboration"
              description="Invite your team members. Manage roles and permissions."
            />
            <FeatureCard
              icon={<ApiIcon />}
              title="Simple API"
              description="Export your data anytime. Full API access for custom integrations."
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">
              Simple, transparent pricing
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Start free, upgrade when you need more.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <PricingPlan
              name="Free"
              price={0}
              requests="5,000"
              features={['Unlimited sites', 'All analytics features', 'Single user']}
            />
            <PricingPlan
              name="Starter"
              price={9}
              requests="15,000"
              features={['Unlimited sites', 'All analytics features', 'Email support']}
              excludedFeatures={['Team collaboration']}
            />
            <PricingPlan
              name="Growth"
              price={19}
              requests="50,000"
              features={['Unlimited sites', 'All analytics features', 'Email support']}
              excludedFeatures={['Team collaboration']}
              highlighted
            />
            <PricingPlan
              name="Pro"
              price={39}
              requests="500,000"
              features={['Unlimited sites', 'All analytics features', 'Priority support', 'Team collaboration', 'White-label']}
            />
          </div>
          <p className="text-center text-gray-500 mt-8">
            Need more? <a href="mailto:hello@statsmatic.io" className="text-primary-600 hover:text-primary-700">Contact us</a> for custom enterprise plans.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="font-semibold text-gray-900">What counts as a request?</h3>
              <p className="mt-2 text-gray-600">
                Each pageview tracked by our script counts as one request. API calls don't count against your limit.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="font-semibold text-gray-900">What happens if I exceed my limit?</h3>
              <p className="mt-2 text-gray-600">
                Your tracking will continue, but you'll receive a notification to upgrade. We never stop tracking mid-month.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="font-semibold text-gray-900">Can I downgrade at any time?</h3>
              <p className="mt-2 text-gray-600">
                Yes, you can downgrade at any time. Your plan will change at the end of your billing period.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="font-semibold text-gray-900">Do I need a cookie banner?</h3>
              <p className="mt-2 text-gray-600">
                No. Statsmatic is privacy-first and doesn't use cookies. You can use it without any cookie consent banners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Get in Touch
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Have a question or feedback? We'd love to hear from you.
            </p>
          </div>

          {contactSubmitted ? (
            <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
              <p className="text-gray-600">
                Thanks for reaching out. We'll get back to you as soon as possible.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <form onSubmit={handleContactSubmit}>
                {/* Honeypot */}
                <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                  <input type="text" name="_fax" tabIndex={-1} autoComplete="off" />
                </div>

                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a topic</option>
                      <option value="General Question">General Question</option>
                      <option value="Technical Support">Technical Support</option>
                      <option value="Billing">Billing</option>
                      <option value="Feature Request">Feature Request</option>
                      <option value="Bug Report">Bug Report</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="How can we help?"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={contactSubmitting}
                    className="w-full"
                  >
                    {contactSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white">
            Ready to get started?
          </h2>
          <p className="mt-4 text-lg text-primary-100">
            Join thousands of indie hackers tracking their SaaS metrics.
          </p>
          <div className="mt-8">
            <Link to="/signup">
              <button className="inline-flex items-center justify-center font-medium rounded-lg px-6 py-3 text-base bg-white text-primary-600 hover:bg-primary-50 transition-colors">
                Start for free
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img src="/logo.png" alt="" className="h-8 w-8" />
              <img src="/logo-text.png" alt="Statsmatic" className="h-6" />
            </Link>
            <div className="mt-4 md:mt-0 text-gray-500 text-sm text-center md:text-right">
              <p>
                &copy; {new Date().getFullYear()}{' '}
                <a href="https://horizon-group.io" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
                  Horizon Group
                </a>
                . All rights reserved.
                <span className="hidden sm:inline"> Built with ❤️ by{' '}
                  <a href="https://jason.camp" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
                    Jason Camp
                  </a>
                </span>
              </p>
              <p className="sm:hidden mt-1">
                Built with ❤️ by{' '}
                <a href="https://jason.camp" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
                  Jason Camp
                </a>
              </p>
              <p className="mt-2">
                <Link to="/privacy" className="hover:text-gray-700">Privacy Policy</Link>
                <span className="mx-2">·</span>
                <Link to="/terms" className="hover:text-gray-700">Terms of Service</Link>
                <span className="mx-2">·</span>
                <Link to="/disclaimer" className="hover:text-gray-700">Disclaimer</Link>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200">
      <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
        {icon}
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-2 text-gray-600">{description}</p>
    </div>
  );
}

function PricingPlan({
  name,
  price,
  requests,
  features,
  excludedFeatures = [],
  highlighted = false,
}: {
  name: string;
  price: number;
  requests: string;
  features: string[];
  excludedFeatures?: string[];
  highlighted?: boolean;
}) {
  return (
    <div
      className={`relative bg-white p-6 rounded-xl border-2 flex flex-col ${
        highlighted ? 'border-primary-500' : 'border-gray-200'
      }`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-primary-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
            Most Popular
          </span>
        </div>
      )}
      <h3 className="text-lg font-semibold text-gray-900">{name}</h3>
      <div className="mt-4">
        <span className="text-4xl font-bold text-gray-900">${price}</span>
        {price > 0 && <span className="text-gray-500">/mo</span>}
      </div>
      <p className="mt-2 text-sm text-gray-500">{requests} requests/month</p>
      <ul className="mt-6 space-y-3 flex-grow">
        {features.map((feature, i) => (
          <li key={i} className="flex items-center text-sm text-gray-600">
            <svg className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {feature}
          </li>
        ))}
        {excludedFeatures.map((feature, i) => (
          <li key={`excluded-${i}`} className="flex items-center text-sm text-gray-400">
            <svg className="h-4 w-4 text-gray-300 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            {feature}
          </li>
        ))}
      </ul>
      <div className="pt-6 mt-auto">
        <Link to="/signup" className="block">
          <Button
            variant={highlighted ? 'primary' : 'secondary'}
            className="w-full"
          >
            Get started
          </Button>
        </Link>
      </div>
    </div>
  );
}

// Icon components
function UnlimitedIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function PrivacyIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function LightweightIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  );
}

function TeamIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function ApiIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  );
}

export default Landing;
